import { useSelectedValueByVariableKey, Value, useMaterials } from '@k3/core'
import { RoundedBox } from '@react-three/drei'
import {
  isEFormClearType,
  isEFormColorType,
  isEFormUnichromeType,
  Keys,
  MaterialNames,
} from '../utility/utils'
import { useDimensions } from './useDimensions'
import { useEffect, useMemo, useState } from 'react'
import { SupportedMaterial } from '@k3/core/src/lib/types/material.interface'
import { loadMaterial } from '../utility/materialLoader'

const BackGlass = ({
  width,
  length,
  thickness,
  productValue,
  offsetZ,
  material,
}: {
  width: number
  length: number
  thickness: number
  offsetZ: number
  // isClear: boolean
  productValue: Value
  material?: SupportedMaterial
}) => {
  return material ? (
    <RoundedBox
      args={[width, length, thickness]}
      position={[width / 2, length / 2, offsetZ]}
      radius={0.0005}
      smoothness={0.5}
      castShadow={!isEFormClearType(productValue)}
      receiveShadow={false}
      material={material}
    />
  ) : null
}

export const BackGlassPanel = () => {
  const {
    length,
    width,
    thickness,
    backGlassOffset,
    backGlassBlackOffset,
    vsgBackGlassOffset,
    vsgOffset,
  } = useDimensions()
  const [materialFolie, setMaterialFolie] = useState<SupportedMaterial>()
  const [materialBackGlas, setMaterialBackGlas] = useState<SupportedMaterial>()
  const materialList = useMaterials()
  const materials = useMemo(
    () => new Map(materialList.map((m) => [m.name, m])),
    [materialList]
  )

  const productValue = useSelectedValueByVariableKey(Keys.EFormType.Variable)

  const isColorOrUnichrom =
    isEFormColorType(productValue) || isEFormUnichromeType(productValue)
  const isClear = isEFormClearType(productValue)

  const showVSG =
    useSelectedValueByVariableKey(Keys.BackGlassVSG.Variable)?.key ===
    Keys.BackGlassVSG.Value.Selected

  const mattVSG =
    useSelectedValueByVariableKey(Keys.BackGlassVSGMatte.Variable)?.key ===
    Keys.BackGlassVSGMatte.Value.Selected

  useEffect(() => {
    let key: string | null = null
    switch (true) {
      case mattVSG && isColorOrUnichrom:
        key = MaterialNames.BackGlass_Folie_Color_Unichrom_Matt
        break
      case !mattVSG && isColorOrUnichrom:
        key = MaterialNames.BackGlass_Folie_Color_Unichrom
        break
      case mattVSG && isClear:
        key = MaterialNames.BackGlass_Folie_Clear_matt
        break
      case !mattVSG && isClear:
        key = MaterialNames.BackGlass_Folie_Clear
        break
    }
    if (key != null && materials.has(key)) {
      loadMaterial(materials.get(key)!, (mat) => {
        mat.envMapIntensity = 0.9
        setMaterialFolie(mat)
      })
    } else {
      setMaterialFolie(undefined)
    }
  }, [isColorOrUnichrom, isClear, mattVSG, materials])

  useEffect(() => {
    if (materials.has(MaterialNames.BackGlas)) {
      loadMaterial(materials.get(MaterialNames.BackGlas)!, (mat) => {
        mat.depthWrite = false
        mat.envMapIntensity = 0.7
        setMaterialBackGlas(mat)
      })
    }
  }, [materials])
  return (
    <>
      <BackGlass
        width={width}
        length={length}
        thickness={thickness}
        offsetZ={backGlassOffset}
        productValue={productValue!}
        material={materialBackGlas}
      />

      {(isEFormColorType(productValue) || isEFormUnichromeType(productValue)) &&
        materialFolie && (
          <mesh
            key={materialFolie.name}
            position={[width / 2.0, length / 2.0, backGlassBlackOffset]}
            castShadow={mattVSG}
            receiveShadow={false}
            material={materialFolie}
          >
            <planeGeometry args={[width, length]} />
          </mesh>
        )}

      {showVSG && (
        <>
          <BackGlass
            width={width}
            length={length}
            thickness={thickness}
            offsetZ={vsgBackGlassOffset}
            productValue={productValue!}
            material={materialBackGlas}
          />

          {isEFormClearType(productValue) && materialFolie && (
            <mesh
              key={materialFolie.name}
              position={[width / 2.0, length / 2.0, vsgOffset]}
              castShadow={mattVSG}
              receiveShadow={false}
              material={materialFolie}
            >
              <planeGeometry args={[width, length]} />
            </mesh>
          )}
        </>
      )}
    </>
  )
}
